import { useState } from 'react';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, Send, Heart, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useSiteContent } from '../utils/useSiteContent';

export default function Contact() {
  const t = useSiteContent('contact.');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please fill in every field');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/contact', { name: name.trim(), email: email.trim(), message: message.trim() });
      toast.success('Message sent — thank you for reaching out.');
      setSent(true);
      setName(''); setEmail(''); setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send right now');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.9) brightness(1.05)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(253, 251, 247, 0.94) 0%, rgba(243, 239, 231, 0.9) 50%, rgba(253, 251, 247, 0.85) 100%)' }} aria-hidden />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center relative">
          <div className="inline-flex items-center gap-3 mb-4">
            <Mail className="w-8 h-8" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }} data-testid="contact-title">
              {t('hero.title', 'Reach Out')}
            </h1>
          </div>
          <p className="text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            {t('hero.subtitle', "Quick questions, feedback, or a note of encouragement — send a short message and I'll get back to you when I can.")}
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 pt-8">
        <div className="p-5 rounded-2xl flex items-start gap-4" style={{ backgroundColor: '#F3EFE7', border: '1px solid #E6E2D8' }} data-testid="contact-intro-notice">
          <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#B8860B' }} />
          <div>
            <p className="text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 600 }}>
              {t('notice.title', 'Have a question or a note of encouragement?')}
            </p>
            <p className="text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D', lineHeight: 1.6 }}>
              {t('notice.body', 'This form is best for quick questions, feedback, or general inquiries. For prayer requests, share on the prayer wall or explore the articles and feelings guide.')}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-6 py-12">
        {sent ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#78A18E15', border: '1px solid #78A18E40' }} data-testid="contact-sent-panel">
            <Heart className="w-12 h-12 mx-auto mb-4" style={{ color: '#78A18E' }} />
            <h2 className="text-2xl mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t('sent.title', 'Your message is on its way')}</h2>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{t('sent.body', "I'll get back to you when I can — usually within a few days.")}</p>
            <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
              <button
                onClick={() => setSent(false)}
                className="px-5 py-2 rounded-full text-sm"
                style={{ fontFamily: 'Nunito, sans-serif', color: '#78A18E', border: '2px solid #78A18E', fontWeight: 600 }}
                data-testid="contact-send-another"
              >Send another message</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 sm:p-10 rounded-3xl space-y-5" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }}>
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Your Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="contact-name" />
            </div>
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Your Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@email.com"
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="contact-email" />
            </div>
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Message</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={6} maxLength={1500}
                placeholder="A short question, feedback, or note of encouragement…"
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="contact-message" />
              <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{message.length} / 1500</p>
            </div>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="contact-submit">
              <Send className="w-4 h-4" />
              <span>{submitting ? 'Sending…' : 'Send Message'}</span>
            </button>
          </form>
        )}
      </section>

      <Footer />
    </div>
  );
}
